import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getBranchStock, BranchStockItem, exportStockExcel, exportStockPDF } from '../../services/stockService';
import { getBranches, Branch } from '../../services/branchService';
import { getCategories } from '../../services/itemService';
import StockAdjustmentModal from './StockAdjustmentModal';
import StockHistoryModal from './StockHistoryModal';
import StockForecastModal from './StockForecastModal';
import LowStockAlert from './LowStockAlert';
import { Package, AlertTriangle, TrendingDown, TrendingUp, Search, Filter, Download, FileSpreadsheet, FileText, BarChart3 } from 'lucide-react';

const StockManagement = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [stockData, setStockData] = useState<BranchStockItem[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedBranch, setSelectedBranch] = useState<number>(currentUser?.branch_id || 0);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [adjustmentModal, setAdjustmentModal] = useState<{ isOpen: boolean; item?: BranchStockItem }>({ isOpen: false });
  const [historyModal, setHistoryModal] = useState<{ isOpen: boolean; item?: BranchStockItem }>({ isOpen: false });
  const [forecastModal, setForecastModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    loadBranches();
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedBranch > 0) {
      loadStockData();
    }
  }, [selectedBranch, selectedCategory, showLowStockOnly]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showExportMenu && !target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const loadBranches = async () => {
    try {
      const data = await getBranches();
      setBranches(data);
      // Set default branch for non-owner users
      if (currentUser?.role !== 'owner' && currentUser?.branch_id) {
        setSelectedBranch(currentUser.branch_id);
      } else if (data.length > 0 && !selectedBranch) {
        setSelectedBranch(data[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load branches:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadStockData = async () => {
    if (!selectedBranch) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await getBranchStock(selectedBranch, {
        category: selectedCategory || undefined,
        low_stock_only: showLowStockOnly,
      });
      setStockData(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load stock data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustStock = (item: BranchStockItem) => {
    setAdjustmentModal({ isOpen: true, item });
  };

  const handleViewHistory = (item: BranchStockItem) => {
    setHistoryModal({ isOpen: true, item });
  };

  const handleAdjustmentComplete = () => {
    setAdjustmentModal({ isOpen: false });
    loadStockData();
  };

  const handleExportExcel = async () => {
    if (!selectedBranch) return;
    setExporting(true);
    setShowExportMenu(false);
    try {
      const blob = await exportStockExcel(selectedBranch, selectedCategory || undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_report_${selectedBranch}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert('Failed to export Excel: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!selectedBranch) return;
    setExporting(true);
    setShowExportMenu(false);
    try {
      const blob = await exportStockPDF(selectedBranch, selectedCategory || undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_report_${selectedBranch}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert('Failed to export PDF: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  // Filter stock data by search query
  const filteredStock = stockData.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate statistics
  const totalItems = filteredStock.length;
  const lowStockCount = filteredStock.filter(item => item.is_low_stock).length;
  const totalStockValue = filteredStock.reduce((sum, item) => sum + (item.qty * item.sell_price), 0);
  const outOfStockCount = filteredStock.filter(item => item.qty === 0).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Stock Management</h1>
          <p className="text-gray-600">Monitor dan kelola stok barang per cabang</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setForecastModal(true)}
            disabled={!selectedBranch}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Forecast
          </button>
          
          <div className="relative export-menu-container">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting || !selectedBranch}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <Download className="w-5 h-5 mr-2" />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            
            {/* Export Dropdown */}
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <button
                  onClick={handleExportExcel}
                  disabled={exporting}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center text-gray-700 rounded-t-lg disabled:opacity-50"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                  Export to Excel
                </button>
                <button
                  onClick={handleExportPDF}
                  disabled={exporting}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center text-gray-700 rounded-b-lg disabled:opacity-50"
                >
                  <FileText className="w-4 h-4 mr-2 text-red-600" />
                  Export to PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Low Stock Alert Banner */}
      <LowStockAlert onRefresh={loadStockData} />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Items</p>
              <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
            </div>
            <Package className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Low Stock</p>
              <p className="text-2xl font-bold text-orange-600">{lowStockCount}</p>
            </div>
            <AlertTriangle className="w-10 h-10 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Out of Stock</p>
              <p className="text-2xl font-bold text-red-600">{outOfStockCount}</p>
            </div>
            <TrendingDown className="w-10 h-10 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Stock Value</p>
              <p className="text-2xl font-bold text-green-600">
                Rp {(totalStockValue / 1000).toFixed(0)}K
              </p>
            </div>
            <TrendingUp className="w-10 h-10 text-green-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Branch Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cabang
            </label>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(Number(e.target.value))}
              disabled={currentUser?.role !== 'owner'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} - {branch.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kategori
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Semua Kategori</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cari Barang
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nama atau SKU..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Low Stock Filter */}
          <div className="flex items-end">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showLowStockOnly}
                onChange={(e) => setShowLowStockOnly(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Hanya Low Stock
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading stock data...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadStockData}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        ) : filteredStock.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No stock data found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nama Barang
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategori
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stok
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Min. Stok
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Harga
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nilai Stok
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStock.map((item) => (
                  <tr key={item.item_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.sku}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {item.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`font-semibold ${
                        item.qty === 0 ? 'text-red-600' :
                        item.is_low_stock ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {item.qty} {item.unit}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {item.min_stock} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.qty === 0 ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          Out of Stock
                        </span>
                      ) : item.is_low_stock ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          Low Stock
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Available
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      Rp {item.sell_price.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                      Rp {(item.qty * item.sell_price).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleViewHistory(item)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="View History"
                      >
                        History
                      </button>
                      {(currentUser?.role === 'owner' || currentUser?.role === 'admin') && (
                        <button
                          onClick={() => handleAdjustStock(item)}
                          className="text-green-600 hover:text-green-900"
                          title="Adjust Stock"
                        >
                          Adjust
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {adjustmentModal.isOpen && adjustmentModal.item && (
        <StockAdjustmentModal
          isOpen={adjustmentModal.isOpen}
          onClose={() => setAdjustmentModal({ isOpen: false })}
          item={adjustmentModal.item}
          branchId={selectedBranch}
          onSuccess={handleAdjustmentComplete}
        />
      )}

      {historyModal.isOpen && historyModal.item && (
        <StockHistoryModal
          isOpen={historyModal.isOpen}
          onClose={() => setHistoryModal({ isOpen: false })}
          item={historyModal.item}
          branchId={selectedBranch}
        />
      )}

      {forecastModal && (
        <StockForecastModal
          isOpen={forecastModal}
          onClose={() => setForecastModal(false)}
          branchId={selectedBranch}
          branchName={branches.find(b => b.id === selectedBranch)?.name || ''}
        />
      )}
    </div>
  );
};

export default StockManagement;